import { useState } from 'react'

/**
 * Estado de tela que sobrevive a sair e voltar.
 *
 * O bate-papo perdia a conversa aberta ao navegar para outra tela: React
 * desmonta o componente e o `useState` some junto. Voltar e ter de procurar
 * de novo onde se estava é o tipo de atrito que aparece dezenas de vezes por
 * dia em quem fica alternando entre conversa e lead.
 *
 * `sessionStorage`, e não `localStorage`, por dois motivos: some quando a aba
 * fecha — voltar amanhã numa conversa de ontem seria estranho — e cada aba
 * guarda a sua, então dá para deixar dois atendimentos abertos lado a lado
 * sem um sobrescrever o outro.
 *
 * Falha em silêncio: em navegação privada ou com armazenamento bloqueado, o
 * `sessionStorage` lança. Uma tela que não lembra onde estava é chata; uma
 * tela que não abre é pior.
 */
export function useEstadoLembrado<T>(chave: string, inicial: T) {
  const [valor, setValor] = useState<T>(() => {
    try {
      const guardado = sessionStorage.getItem(chave)
      return guardado === null ? inicial : (JSON.parse(guardado) as T)
    } catch {
      return inicial
    }
  })

  const guardar = (novo: T | ((atual: T) => T)) => {
    setValor((atual) => {
      const proximo = typeof novo === 'function' ? (novo as (a: T) => T)(atual) : novo
      try {
        sessionStorage.setItem(chave, JSON.stringify(proximo))
      } catch {
        // Sem armazenamento a tela continua funcionando, só não lembra.
      }
      return proximo
    })
  }

  return [valor, guardar] as const
}
